import React, { useEffect, useState } from 'react';
import { ShowAndHidePin } from './ShowAndHidePin';
import { AuthContainer } from './AuthContainer';
import {Loading, LoadingCircle} from '../Loading';
import { StateType } from '../../types/pinState';
import { useUserContext } from '../../context/user';
import { useSignOut } from '../../hooks/useSignOut';
import { useOwnProfileQuery } from '../../hooks/useOwnProfileQuery';
import { AuthType } from '../../types/auth';
import { LayoutType } from "../../types/common/layout";

const pin_not_equal_error = "Пин-коды не совпадают";
const pin_incorrect_error = "Пин-код неверный";
const pin_not_format_error = "Пин-код должен состоять только из цифр";
const required_field = "*обязательное поле";
const failed_get_profile_error = "Не удается получить информацию о профиле, попробуйте позже";

export const PinInput = () => {

    const { userPinData, setUserPin, lastPageUrl, setLastPageUrl, setLastEnteredUserPinDate, userUuid, setUserUuid, userAuthType, isEnteredPinDate, setLayout } = useUserContext();

    const [verificationStatus, setVerificationStatus] = useState('idle');
    const [errorMessage, setErrorMessage] = useState("");
    const [originalPin, setOriginalPin] = useState("");
    const [pin, setPin] = useState("");
    const [state, setState] = useState(StateType.CREATE);
    const [shouldFetchProfile, setShouldFetchProfile] = useState(false);
    const [isLoading, setLoading] = useState(true);
    const signOut = useSignOut()
    const {
        data: profile,
        isSuccess: isProfileSuccessFetched,
        isError: isProfileFailedFetch
    } = useOwnProfileQuery(shouldFetchProfile);
    const numInputs = 6;

    useEffect(() => {
        setInitialState(true, userUuid)
    }, []);

    useEffect(() => {
        if (isProfileSuccessFetched && !userUuid) {
            // Сохраняем айди что бы была возможность привязать его и пинкод к пользователю
            setUserUuid(profile?.pk)
            setLoading(false);
            setInitialState(false, profile?.pk)
        }
    }, [profile]);

    useEffect(() => {
        if (isProfileFailedFetch) {
            setErrorMessage(failed_get_profile_error);
            setLoading(false);
            return
        }
    }, [isProfileFailedFetch]);

    const setInitialState = (isFirst: boolean, userId: string | null) => {
        switch (userAuthType) {
            case AuthType.LOGIN:
                // Если залогиненный через логин сюда попадет (введя адрес вручную), то вернуть на главную
                window.location.replace('/');
                break
            default:
                if (isEnteredPinDate()) {
                    // NOTE: Disable layout 
                    handleSelectLayout('MANAGER')
                    // NOTE: Disable layout
                    window.location.replace('/');
                    return
                }
                if (userPinData()) {
                    setState(StateType.SIGN_IN)
                    setLoading(false);
                    return
                }

                if (userId) {
                    // Id пользователя есть, пин код еще не был создан
                    setState(StateType.CREATE)
                    setLoading(false);
                } else {
                    if (isFirst) {
                        // Загружаем профиль потому что нужен ID пользователя для сохранения и получения пина и потом уже попробовать найти пин код
                        setShouldFetchProfile(true)
                    } else {
                        // Не удается получить информацию о пользователе
                        setErrorMessage(failed_get_profile_error);
                        setLoading(false);
                    }
                }
        }
    };

    const submit = () => {
        switch (state) {
            case StateType.CREATE:
                handleCreatingPinSubmit()
                break;
            case StateType.CONFIRM:
                handleConfirmedPinSubmit()
                break;
            case StateType.SIGN_IN:
                handleSignInPinSubmit()
                break;
            default:
                break;
        }
    }

    const handleCreatingPinSubmit = () => {
        if (isValidPin(pin)) {
            setOriginalPin(pin);
            resetField(false);
            setState(StateType.CONFIRM);
        }
    }

    const handleConfirmedPinSubmit = () => {
        if (isValidPin(pin)) {
            if (originalPin === pin) {
                setUserPin(pin)
                resetField(false);

                setVerificationStatus('success');
            } else {
                setErrorMessage(pin_not_equal_error);
            }
        }
    }

    const handleSignInPinSubmit = () => {
        if (isValidPin(pin)) {
            if (userPinData()?.userPin === pin) {
                setVerificationStatus('success');
            } else {
                setErrorMessage(pin_incorrect_error);
            }
        }
    }

    const isValidPin = (pin: string) => {
        // Работает лучше чем Number т.к пропускает 000000
        function isNumeric(value: string): boolean {
            return /^\d+$/.test(value);
        }

        if (pin.length === 0) {
            setErrorMessage(required_field);
            return false
        }
        if (pin.length === numInputs && verificationStatus !== 'success') {
            if (isNumeric(pin)) {
                return true
            } else {
                setErrorMessage(pin_not_format_error);
                return false
            }
        }
        return false
    }

    // NOTE: Disable layout 
    const handleSelectLayout = (layout: LayoutType) => {
        setLayout(layout);
    }
    // NOTE: Disable layout 

    useEffect(() => {
        if (verificationStatus === 'success') {
            // NOTE: Disable layout 
            handleSelectLayout('MANAGER')
            // NOTE: Disable layout
            setLastEnteredUserPinDate()
            if (lastPageUrl) {
                window.location.replace(lastPageUrl);
                setLastPageUrl(null);
            } else {
                window.location.replace('/');
            }
        }
    }, [verificationStatus]);


    const onChange = (value: string) => {
        if (value.length <= numInputs) {
            setPin(value)
        }
    }

    const resetField = (isNeedResetConfirmedPin: boolean) => {
        setErrorMessage('');
        setPin('');
        if (isNeedResetConfirmedPin) {
            setOriginalPin('')
        }
    }

    const getStateString = () => {
        switch (state) {
            case StateType.CREATE:
                return "Задайте пин-код";
            case StateType.CONFIRM:
                return "Повторите пин-код";
            case StateType.SIGN_IN:
                return "Введите пин-код";
            default:
                return "";
        }
    }

    if (isLoading) return (
        <AuthContainer>
            <div>
                <LoadingCircle />
            </div>
        </AuthContainer>
    )

    return (
        <AuthContainer>
            {isLoading ? <Loading /> :
                <div className="w-full">

                    <div
                        className="mb-0 font-manager-acrom-h4-24-medium-acrom font-[number:var(--manager-acrom-h4-24-medium-acrom-font-weight)] text-black text-[length:var(--manager-acrom-h4-24-medium-acrom-font-size)] text-center tracking-[var(--manager-acrom-h4-24-medium-acrom-letter-spacing)] leading-[var(--manager-acrom-h4-24-medium-acrom-line-height)] [font-style:var(--manager-acrom-h4-24-medium-acrom-font-style)]">
                        Вход
                    </div>
                    <div
                        className='w-full mb-4 text-center flex flex-col items-center justify-center mt-4 text-sm'>
                        <div className="text-gray-400">
                            {getStateString()}
                        </div>
                    </div>
                    <ShowAndHidePin
                        pin={pin}
                        setPin={onChange}
                        containerStyles={errorMessage === required_field ? "border-2 border-red-300" : ""}
                    />

                    {errorMessage !== "" ? <span
                        className='text-center flex items-center justify-center text-red-500 text-sm mt-1 mb-2'>{errorMessage}</span> : ""}

                    <div className="w-full flex flex-col justify-center items-center">
                        <button
                            onClick={submit}
                            className={`
                            w-full h-12 
                            inline-flex 
                            items-center 
                            justify-center flex-[0_0_auto] 
                            rounded-lg gap-2.5 px-5 py-4 relative 
                            font-manager-acrom-ph1-18-medium-acrom 
                            font-[number:var(--manager-acrom-ph1-18-medium-acrom-font-weight)] 
                            text-[#979797] 
                            text-[length:var(--manager-acrom-ph1-18-medium-acrom-font-size)] 
                            text-center 
                            tracking-[var(--manager-acrom-ph1-18-medium-acrom-letter-spacing)] 
                            leading-[var(--manager-acrom-ph1-18-medium-acrom-line-height)] 
                            [font-style:var(--manager-acrom-ph1-18-medium-acrom-font-style)]
                            ${pin.length === numInputs ? "bg-melon-yellow text-melon-black" : "bg-melon-light-grey"}
                            `}
                        >
                            Далее
                        </button>

                        <a
                            href="mailto:report@melonfashion.com"
                            className="mt-10 font-manager-acrom-links font-[number:var(--manager-acrom-links-font-weight)] text-black text-[length:var(--manager-acrom-links-font-size)] text-center tracking-[var(--manager-acrom-links-letter-spacing)] leading-[var(--manager-acrom-links-line-height)] underline whitespace-nowrap [font-style:var(--manager-acrom-links-font-style)]"
                        >Связаться с тех. поддержкой</a>

                        <button
                            onClick={signOut}
                            className="mt-5 font-manager-acrom-links font-[number:var(--manager-acrom-links-font-weight)] text-black text-[length:var(--manager-acrom-links-font-size)] text-center tracking-[var(--manager-acrom-links-letter-spacing)] leading-[var(--manager-acrom-links-line-height)] underline whitespace-nowrap [font-style:var(--manager-acrom-links-font-style)]">
                            Выход из аккаунта
                        </button>
                    </div>
                </div>
            }
        </AuthContainer>
    );
};